import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { loandepotLogo } from '@ld.consumer-portal/data-uri';
import { jsPDF } from 'jspdf';
import { LoggingService } from './logging.service';

const PAGE_SIZES = {
  LETTER: { width: 612, height: 792 }, // 8.5 x 11 inches
  LEGAL: { width: 612, height: 1008 }, // 8.5 x 14 inches
  A4: { width: 595, height: 842 }, // 210 x 297 mm
} as const;

const MARGINS = {
  top: 60,
  bottom: 60,
  left: 40,
  right: 40,
};

const COLORS = {
  black: '#000000',
  border: '#cccccc',
};

export interface PdfTableColumn {
  header: string;
  key: string;
  width?: number | string | '*';
  align?: 'left' | 'center' | 'right';
  format?: (value: unknown) => string;
}

export interface PdfBorrowerInfo {
  name: string;
  addressLine1: string;
  addressLine2: string;
}

export interface PdfGeneratorOptions {
  title: string;
  subtitle?: string;
  columns: PdfTableColumn[];
  data: Record<string, unknown>[];
  filename?: string;
  orientation?: 'portrait' | 'landscape';
  pageSize?: 'LETTER' | 'LEGAL' | 'A4';
  footerText?: string;
  generatedDate?: Date;
  borrowerInfo?: PdfBorrowerInfo;
}

@Injectable({
  providedIn: 'root',
})
export class PdfGeneratorService {
  private readonly loggingService = inject(LoggingService);
  async print(options: PdfGeneratorOptions): Promise<void> {
    const doc = this.createPdf(options);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  }

  async download(options: PdfGeneratorOptions): Promise<void> {
    const doc = this.createPdf(options);
    const filename = options.filename || `${options.title.replace(/\s+/g, '_')}.pdf`;

    if (Capacitor.isNativePlatform()) {
      await this.downloadMobile(doc, filename);
    } else {
      doc.save(filename);
    }
  }

  private async downloadMobile(doc: jsPDF, filename: string): Promise<void> {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ]);

    try {
      const base64 = doc.output('datauristring').split(',')[1];

      const result = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Documents,
      });

      await Share.share({
        title: filename,
        url: result.uri,
        dialogTitle: 'Save or Share PDF',
      });
    } catch (error) {
      this.loggingService.logException(error, 'Failed to save PDF on mobile:');
      throw error;
    }
  }

  async preview(options: PdfGeneratorOptions): Promise<void> {
    const doc = this.createPdf(options);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  async getBlob(options: PdfGeneratorOptions): Promise<Blob> {
    const doc = this.createPdf(options);
    return doc.output('blob');
  }

  async getBase64(options: PdfGeneratorOptions): Promise<string> {
    const doc = this.createPdf(options);
    return doc.output('datauristring');
  }

  private createPdf(options: PdfGeneratorOptions): jsPDF {
    const pageSize = PAGE_SIZES[options.pageSize || 'LETTER'];
    const isLandscape = options.orientation === 'landscape';
    const width = isLandscape ? pageSize.height : pageSize.width;
    const height = isLandscape ? pageSize.width : pageSize.height;

    const doc = new jsPDF({
      orientation: options.orientation || 'portrait',
      unit: 'pt',
      format: [width, height],
    });

    const contentWidth = width - MARGINS.left - MARGINS.right;

    this.drawHeader(doc);

    let yPos = 70;

    if (options.borrowerInfo) {
      yPos = this.drawBorrowerInfo(doc, options.borrowerInfo, yPos);
    }

    yPos = this.drawTitleSection(doc, options, yPos);

    this.drawTable(doc, options, yPos, contentWidth, height);

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      this.drawFooter(doc, options.footerText || '', i, pageCount, height);
    }

    return doc;
  }

  private drawHeader(doc: jsPDF): void {
    doc.addImage(loandepotLogo, 'PNG', MARGINS.left, 10, 120, 50);
  }

  private drawBorrowerInfo(doc: jsPDF, borrowerInfo: PdfBorrowerInfo, yPos: number): number {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(COLORS.black);

    yPos += 20;
    doc.text(borrowerInfo.name.toUpperCase(), MARGINS.left, yPos);

    yPos += 12;
    doc.text(borrowerInfo.addressLine1.toUpperCase(), MARGINS.left, yPos);

    yPos += 12;
    doc.text(borrowerInfo.addressLine2.toUpperCase(), MARGINS.left, yPos);

    yPos += 10;

    return yPos;
  }

  private drawTitleSection(doc: jsPDF, options: PdfGeneratorOptions, yPos: number): number {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(COLORS.black);
    const titleText = options.title;
    doc.text(titleText, MARGINS.left, yPos);

    const titleWidth = doc.getTextWidth(titleText);
    doc.setDrawColor(COLORS.black);
    doc.setLineWidth(0.5);
    doc.line(MARGINS.left, yPos + 2, MARGINS.left + titleWidth, yPos + 2);

    yPos += 20;

    if (options.subtitle) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(COLORS.black);
      doc.text(options.subtitle, MARGINS.left, yPos);
      yPos += 16;
    }

    yPos += 10;

    return yPos;
  }

  private drawTable(
    doc: jsPDF,
    options: PdfGeneratorOptions,
    startY: number,
    contentWidth: number,
    pageHeight: number
  ): number {
    const rowHeight = 28;
    const cellPadding = 8;
    const columns = options.columns;
    const data = options.data;

    const columnWidths = this.calculateColumnWidths(columns, contentWidth);

    let yPos = startY;

    yPos = this.drawTableHeader(doc, columns, columnWidths, yPos, rowHeight, cellPadding);

    for (const row of data) {
      if (yPos + rowHeight > pageHeight - MARGINS.bottom) {
        doc.addPage();
        yPos = MARGINS.top;
        this.drawHeader(doc);
        yPos = this.drawTableHeader(doc, columns, columnWidths, yPos + 20, rowHeight, cellPadding);
      }

      yPos = this.drawTableRow(doc, columns, columnWidths, row, yPos, rowHeight, cellPadding);
    }

    doc.setDrawColor(COLORS.black);
    doc.setLineWidth(1);
    doc.line(MARGINS.left, yPos, MARGINS.left + contentWidth, yPos);

    return yPos;
  }

  private calculateColumnWidths(columns: PdfTableColumn[], contentWidth: number): number[] {
    let fixedTotal = 0;
    let flexCount = 0;
    const fixedWidths: (number | null)[] = [];

    for (const col of columns) {
      if (typeof col.width === 'number') {
        fixedWidths.push(col.width);
        fixedTotal += col.width;
      } else {
        fixedWidths.push(null);
        flexCount++;
      }
    }

    const flexWidth = flexCount > 0 ? (contentWidth - fixedTotal) / flexCount : 0;

    return fixedWidths.map((w) => w ?? flexWidth);
  }

  private drawTableHeader(
    doc: jsPDF,
    columns: PdfTableColumn[],
    columnWidths: number[],
    yPos: number,
    rowHeight: number,
    cellPadding: number
  ): number {
    const totalWidth = columnWidths.reduce((sum, w) => sum + w, 0);

    doc.setDrawColor(COLORS.border);
    doc.setLineWidth(0.5);
    doc.line(MARGINS.left, yPos, MARGINS.left + totalWidth, yPos);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(COLORS.black);

    let xPos = MARGINS.left;
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      const textX = this.getTextX(xPos, columnWidths[i], cellPadding, col.align || 'left');
      doc.text(col.header, textX, yPos + rowHeight / 2 + 4, {
        align: col.align || 'left',
      });
      xPos += columnWidths[i];
    }

    yPos += rowHeight;

    doc.line(MARGINS.left, yPos, MARGINS.left + totalWidth, yPos);

    return yPos;
  }

  private drawTableRow(
    doc: jsPDF,
    columns: PdfTableColumn[],
    columnWidths: number[],
    row: Record<string, unknown>,
    yPos: number,
    rowHeight: number,
    cellPadding: number
  ): number {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(COLORS.black);

    let xPos = MARGINS.left;
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      const value = row[col.key];
      const formattedValue = col.format ? col.format(value) : String(value ?? '');

      const textX = this.getTextX(xPos, columnWidths[i], cellPadding, col.align || 'left');
      doc.text(formattedValue, textX, yPos + rowHeight / 2 + 4, {
        align: col.align || 'left',
      });
      xPos += columnWidths[i];
    }

    yPos += rowHeight;

    const totalWidth = columnWidths.reduce((sum, w) => sum + w, 0);
    doc.setDrawColor(COLORS.border);
    doc.setLineWidth(0.5);
    doc.line(MARGINS.left, yPos, MARGINS.left + totalWidth, yPos);

    return yPos;
  }

  private getTextX(cellX: number, cellWidth: number, padding: number, align: string): number {
    switch (align) {
      case 'center':
        return cellX + cellWidth / 2;
      case 'right':
        return cellX + cellWidth - padding;
      default:
        return cellX + padding;
    }
  }

  private drawFooter(doc: jsPDF, footerText: string, currentPage: number, pageCount: number, pageHeight: number): void {
    const yPos = pageHeight - 30;
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(COLORS.black);

    if (footerText) {
      doc.text(footerText, MARGINS.left, yPos);
    }

    doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth - MARGINS.right, yPos, {
      align: 'right',
    });
  }

  createPaymentHistoryOptions(
    payments: Array<{
      paymentDate: Date;
      description: string;
      amount: number;
    }>,
    loanNumber?: string
  ): PdfGeneratorOptions {
    return {
      title: 'Payment Activity',
      subtitle: loanNumber ? `Loan Number: ${loanNumber}` : undefined,
      filename: 'Mello_Serv.pdf',
      columns: [
        {
          header: 'Payment Date',
          key: 'paymentDate',
          format: (value) => {
            const date = value as Date;
            const d = new Date(date);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
          },
        },
        {
          header: 'Description',
          key: 'description',
          width: '*',
        },
        {
          header: 'Amount',
          key: 'amount',
          format: (value) => {
            const amount = value as number;
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(amount);
          },
        },
      ],
      data: payments,
    };
  }
}
